import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title as ChartTitle,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import {
  PageSection,
  ControlContainerRow,
  ControlContainer,
  Subtitle,
  Caption,
  StyledCard,
} from 'common/styles/page';

import { useApiPortalV1AssessmentsResultsListQuery } from 'areas/assessment-schedule/api';
import { useGetAssessmentPeriodMetricBenchmarkResultsQuery } from 'areas/assessment-schedule/api';
import styled from 'styled-components';
import AnalyticsOverviewBarChart, { BarChartData } from './AnalyticsOverviewBarChart';
import DoughnutGauge from 'areas/dashboard/components/DoughnutGauge';
import CategorySelect from 'common/components/CategorySelect';

import { ChartDataset } from 'chart.js';
import { Plugin } from 'chart.js';
import AnalyticsOverviewRadarChart from './AnalyticsOverviewRadarChart';
import { usePageCopilotPrompts } from 'areas/copilot/components/CopilotPromptsContext';
import { AggregateType } from 'common/constants/AggregateType';
import {
  getSelectedAssessmentPeriodCategories,
  getPreviousAssessmentPeriodId,
  getSelectedAssessmentPeriod,
  getSelectedAssessmentPeriodId,
  isNulogicBenchmarksEnabled,
} from 'common/selectors/auth';

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTitle, Tooltip, Legend);

interface AnalyticsOverviewBarProps {
  type: string;
}

const AnalyticsOverviewBar: React.FC<AnalyticsOverviewBarProps> = ({ type }) => {
  const assessmentPeriodId = useSelector(getSelectedAssessmentPeriodId);
  const previousPeriodId = useSelector(getPreviousAssessmentPeriodId);
  const assessmentPeriod = useSelector(getSelectedAssessmentPeriod);
  const assessmentPeriodCategories = useSelector(getSelectedAssessmentPeriodCategories);
  const isBenchmarkFeatureEnabled = useSelector(isNulogicBenchmarksEnabled);
  const assessmentPeriodName = assessmentPeriod?.name;

  const { data: data_by_category } = useApiPortalV1AssessmentsResultsListQuery({
    aggregateType: AggregateType.CATEGORY_OVERALL,
    assessmentPeriodId: assessmentPeriodId,
  });
  const { data: data_by_option } = useApiPortalV1AssessmentsResultsListQuery({
    aggregateType: AggregateType.CATEGORY_PILLARS,
    assessmentPeriodId: assessmentPeriodId,
  });

  const { data: prior_data_by_option } = useApiPortalV1AssessmentsResultsListQuery(
    {
      aggregateType: AggregateType.CATEGORY_PILLARS,
      assessmentPeriodId: previousPeriodId,
    },
    {
      skip: !previousPeriodId,
    }
  );

  const { data: benchmarkData } = useGetAssessmentPeriodMetricBenchmarkResultsQuery(
    { assessmentPeriodId: assessmentPeriodId },
    { skip: !assessmentPeriodId || !isBenchmarkFeatureEnabled }
  );

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedCategoryName, setSelectedCategoryName] = useState<string | null>(null);
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [selectedOptionName, setSelectedOptionName] = useState<string | null>(null);
  const [currentPeriodName, setCurrentPeriodName] = useState<string>('Current Period');
  const [priorPeriodName, setPriorPeriodName] = useState<string>('Prior Period');

  const [pillarData, setPillarData] = useState<BarChartData[]>([]);
  const [priorPillarData, setPriorPillarData] = useState<BarChartData[]>([]);

  type CustomBarDataset = ChartDataset<'bar'> & {
    nValues?: number[];
  };

  function adjustOpacity(rgba: string, newOpacity: number) {
    return rgba.replace(/rgba\(([^)]+),[^)]+\)/, `rgba($1, ${newOpacity})`);
  }

  const [barChartData, setBarChartData] = useState({
    labels: [],
    ids: [],
    datasets: [
      {
        label: 'Scores',
        data: [],
        nValues: [],
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
      },
    ],
  });
  const [barColors, setBarColors] = useState<string[]>([]);
  const [borderColors, setBorderColors] = useState<string[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [selectedScore, setSelectedScore] = useState<number | null>(null);
  const [showBenchmarkLine, setShowBenchmarkLine] = useState(false);
  const { setPagePrompts, setPageInfoItems, setPageInfoTitle, setPageInfoIntro } = usePageCopilotPrompts();

  useEffect(() => {
    if (!assessmentPeriodCategories || assessmentPeriodCategories.length === 0) {
      setSelectedCategory(null);
      setSelectedCategoryName(null);
      return;
    }

    const sortedCategories = [...assessmentPeriodCategories].sort((a: any, b: any) =>
      a.name.localeCompare(b.name)
    );

    const selectedCategoryStillExists = sortedCategories.some(
      (category: any) => category.id === selectedCategory
    );
    const primaryCategory = sortedCategories.find((category: any) => category.priority === 'primary');
    const defaultCategoryId = primaryCategory?.id || sortedCategories[0]?.id || null;

    const effectiveCategoryId = !selectedCategory || !selectedCategoryStillExists
      ? defaultCategoryId
      : selectedCategory;

    if (effectiveCategoryId !== selectedCategory) {
      setSelectedCategory(effectiveCategoryId);
    }

    const effectiveCategoryName = sortedCategories.find((category: any) => category.id === effectiveCategoryId)?.name || null;
    if (effectiveCategoryName !== selectedCategoryName) {
      setSelectedCategoryName(effectiveCategoryName);
    }
  }, [assessmentPeriodCategories, selectedCategory]);

  useEffect(() => {
    if (data_by_category && selectedCategory) {
      const filteredData = data_by_category[0]?.result.aggregate_scores.filter(
        (score: { category_id: string }) => score.category_id === selectedCategory
      );

      const backgroundColors = filteredData.map(
        (score: { color: string }) => 'rgba(135, 206, 250, 0.4)'
      );
      const borderColors = filteredData.map(
        (score: { color: string }) => 'rgba(135, 206, 250, 0.8)'
      );

      const updatedBarChartData = {
        labels: filteredData.map((score: { option_name: string }) => score.option_name),
        ids: filteredData.map((score: { option_id: string }) => score.option_id),
        datasets: [
          {
            label: 'Scores',
            data: filteredData.map((score: { overall_score: number }) =>
              score.overall_score.toFixed(1)
            ),
            nValues: filteredData.map((score: { average_n: number }) => score.average_n),
            backgroundColor: backgroundColors,
            borderColor: borderColors,
          },
        ],
      };
      setBarChartData(updatedBarChartData);
      setBarColors(backgroundColors);
      setBorderColors(borderColors);

      setSelectedOptionId(updatedBarChartData?.ids[0]);
      setSelectedOptionName(updatedBarChartData?.labels[0]);
      setSelectedScore(filteredData?.[0]?.overall_score ?? null);
      setSelectedIndex(0);
    }
  }, [data_by_category, selectedCategory]);

  useEffect(() => {
    const categoryLabel = selectedCategoryName || 'the selected category';

    setPagePrompts([
      {
        title: 'Organization Summary Review',
        displayText: `Summarize the organization culture score breakdown by ${categoryLabel}.`,
        prompt:
          `Review the Organization Summary page for ${categoryLabel} and explain the culture score breakdown across the category options. Highlight the strongest areas, the biggest gaps in the data, and what changed since the prior assessment. Offer clear next steps for leaders to act on these insights.`,
      },
      {
        title: 'Score Strengths and Risks',
        displayText: `What are the top strengths and risk areas by ${categoryLabel}?`,
        prompt:
          `Based on the category data shown for ${categoryLabel}, identify the top strengths and the riskiest areas. Include which demographic groups or option scores are driving each point and why those results matter.`,
      },
      {
        title: 'Executive Actions',
        displayText: `Recommend action steps from ${categoryLabel} scores.`,
        prompt:
          `Provide practical leadership recommendations based on the ${categoryLabel} summary. Focus on closing gaps, reinforcing strengths, and aligning the next assessment with measurable improvements.`,
      },
    ]);

    setPageInfoTitle('About Organization Summary');
    setPageInfoIntro(
      'This section provides a breakdown of current culture scores by category options and compares them with prior period performance.',
    );
    setPageInfoItems([
      {
        term: 'Category breakdown',
        description: 'View how each category option contributes to the overall culture score.',
      },
      {
        term: 'Performance comparison',
        description: 'Compare current scores against the prior assessment period.',
      },
      {
        term: 'Action guidance',
        description: 'Use the data to identify where leaders should focus improvement efforts.',
      },
    ]);

    return () => {
      setPagePrompts([]);
      setPageInfoItems([]);
      setPageInfoTitle(undefined);
      setPageInfoIntro(undefined);
    };
  }, [setPagePrompts, setPageInfoItems, setPageInfoTitle, setPageInfoIntro, selectedCategoryName]);

  useEffect(() => {
    if (data_by_option && selectedOptionId) {
      const filteredOptionData = data_by_option[0]?.result?.pillars.filter(
        (score: { option_id: string }) => score.option_id === selectedOptionId
      );

      const formattedPillarData =
        filteredOptionData?.map((score: { pillar_name: string; overall_score: number }) => ({
          label: score.pillar_name,
          value: parseFloat(score.overall_score.toFixed(1)), // ✅ ensure number
        })) || [];

      // Always set current period pillar data first
      setPillarData(formattedPillarData);

      // Set period names
      setCurrentPeriodName(data_by_option?.[0]?.result.assessment_period_name || 'Current Period');
      setPriorPeriodName(
        prior_data_by_option?.[0]?.result.assessment_period_name || 'Prior Period'
      );

      // If prior data exists, compute percent change and set priorPillarData
      if (prior_data_by_option) {
        const filteredPriorOptionData = prior_data_by_option[0]?.result?.pillars.filter(
          (score: { option_id: string }) => score.option_id === selectedOptionId
        );

        const percentChangeData = formattedPillarData.map(
          (current: { label: string; value: number }) => {
            const prior = filteredPriorOptionData?.find(
              (p: { pillar_name: string }) => p.pillar_name === current.label
            );

            const priorScore = prior ? parseFloat(prior.overall_score.toFixed(1)) : null;

            const percent_change =
              priorScore && priorScore !== 0
                ? ((current.value - priorScore) / priorScore) * 100
                : null;

            return {
              ...current,
              change:
                percent_change !== null && isFinite(percent_change)
                  ? parseFloat(percent_change.toFixed(1))
                  : null,
            };
          }
        );

        setPillarData(percentChangeData);

        const formattedPriorData =
          filteredPriorOptionData?.map((score: { pillar_name: string; overall_score: number }) => ({
            label: score.pillar_name,
            value: parseFloat(score.overall_score.toFixed(1)), // ✅ ensure number
          })) || [];

        setPriorPillarData(formattedPriorData);
      } else {
        setPriorPillarData([]);
      }
    }
  }, [data_by_option, prior_data_by_option, selectedOptionId]);

  const datasets: any[] = [
    {
      label: 'Scores',
      data: barChartData.datasets[0].data,
      nValues: barChartData.datasets[0].nValues,
      backgroundColor: barChartData.labels.map((_, i) => {
        const baseColor = barColors[i];
        if (i === selectedIndex) {
          return adjustOpacity(baseColor, 0.9);
        }
        return baseColor;
      }),
      borderWidth: barChartData.labels.map((_, i) => (i === selectedIndex ? 1 : 2)),
      borderColor: borderColors,
    },
  ];

  const benchmarkMean = isBenchmarkFeatureEnabled ? benchmarkData?.overall_score?.mean : undefined;
  const hasBenchmarkMean = typeof benchmarkMean === 'number' && Number.isFinite(benchmarkMean);
  const benchmarkPillars = isBenchmarkFeatureEnabled ? benchmarkData?.pillars || [] : [];
  const benchmarkPillarData: Array<{ label: string; value: number | null }> = pillarData.map(
    currentPillar => {
      const benchmarkPillar = benchmarkPillars.find(
        pillar => pillar.label === currentPillar.label
      );
      const benchmarkValue = benchmarkPillar?.mean;

      return {
        label: currentPillar.label,
        value:
          typeof benchmarkValue === 'number' && Number.isFinite(benchmarkValue)
            ? parseFloat(benchmarkValue.toFixed(1))
            : null,
      };
    }
  );
  const visibleRadarBenchmarkPillarData =
    isBenchmarkFeatureEnabled && showBenchmarkLine ? benchmarkPillarData : [];

  const benchmarkLineOverlayPlugin: Plugin<'bar'> = {
    id: 'benchmarkLineOverlay',
    afterDraw: (chart, _args, pluginOptions) => {
      const options = (pluginOptions || {}) as {
        enabled?: boolean;
        value?: number;
        color?: string;
      };

      if (!options.enabled || typeof options.value !== 'number' || !Number.isFinite(options.value)) {
        return;
      }

      const yScale = chart.scales.y;
      const chartArea = chart.chartArea;
      if (!yScale || !chartArea) {
        return;
      }

      const y = yScale.getPixelForValue(options.value);

      const ctx = chart.ctx;
      ctx.save();
      ctx.beginPath();
      ctx.setLineDash([6, 6]);
      ctx.lineWidth = 2;
      ctx.strokeStyle = options.color || 'rgba(230, 101, 121, 0.95)';
      ctx.moveTo(chartArea.left, y);
      ctx.lineTo(chartArea.right, y);
      ctx.stroke();
      ctx.restore();
    },
  };

  const chartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      benchmarkLineOverlay: {
        enabled: hasBenchmarkMean && showBenchmarkLine,
        value: benchmarkMean,
        color: 'rgba(230, 101, 121, 0.95)',
      },
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (tooltipItem: any) => {
            const dataset = tooltipItem.dataset as CustomBarDataset;
            const index = tooltipItem.dataIndex;
            const value = dataset.data[index];
            const n = dataset.nValues?.[index] ?? 0;
            return `Score: ${value} (${n} respondents)`;
          },
        },
      },
    },
    onClick: (evt: any, elements: any[]) => {
      if (!elements || elements.length === 0) return;
      const { index } = elements[0];
      setSelectedIndex(index);
      setSelectedOptionId(barChartData.ids[index]);
      setSelectedOptionName(barChartData.labels[index]);

      const clickedLabel = barChartData.ids[index];
      const found = data_by_category?.[0]?.result.aggregate_scores.find(
        (score: { category_id: string; option_id: string }) =>
          score.category_id === selectedCategory && score.option_id === clickedLabel
      );
      setSelectedScore(found?.overall_score ?? null);
    },
    scales: {
      y: {
        min: 0,
        max: 10,
      },
    },
  };

  return (
    <>
      <AnalyticsOverviewRow>
        <LeftColumn>
          <StyledCard>
            <ControlContainerRow padbottom="10px" padright="10px" padleft="20px" padtop="10px">
              <ControlContainer justify="left">
                <Subtitle>{`${assessmentPeriodName}`}</Subtitle>
              </ControlContainer>
              <ControlContainer justify="right">
                <TopRightControls>
                  <CategorySelect
                    categories={assessmentPeriodCategories}
                    selectedCategory={selectedCategory}
                    onCategoryChange={setSelectedCategory}
                    placeholder="Select Category"
                    width="250px"
                  />
                </TopRightControls>
              </ControlContainer>
            </ControlContainerRow>

            <PageSection
              padbottom
              padleft
              padright
              display="flex"
              flexDirection="row"
              style={{ width: '100%', height: '450px' }}
            >
              <Bar
                plugins={[benchmarkLineOverlayPlugin]}
                options={chartOptions}
                data={{
                  labels: barChartData.labels,
                  datasets: datasets,
                }}
                style={{ height: '100%' }}
              />
            </PageSection>
            {hasBenchmarkMean && (
              <BenchmarkControlRow>
                <BenchmarkToggle
                  role="button"
                  tabIndex={0}
                  aria-label="Toggle nuLogic Benchmark"
                  aria-pressed={showBenchmarkLine}
                  onClick={() => setShowBenchmarkLine(prev => !prev)}
                  onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setShowBenchmarkLine(prev => !prev);
                    }
                  }}
                >
                  {!showBenchmarkLine && (
                    <BenchmarkCheckbox
                      type="checkbox"
                      checked={false}
                      readOnly
                      tabIndex={-1}
                      aria-label="Show nuLogic Benchmark"
                    />
                  )}
                  <BenchmarkLegendSwatch hidden={!showBenchmarkLine} />
                  <span>{`nuLogic Benchmark (${benchmarkMean.toFixed(1)})`}</span>
                </BenchmarkToggle>
              </BenchmarkControlRow>
            )}
          </StyledCard>
        </LeftColumn>
        <RightColumn>
          <RightColumnSection>
            <RightColumnCard>
              <PageSection padtop="5px" padright="15px" padbottom>
                <PageSection display="flex" flexDirection="column">
                  <PageSection flexDirection="column" padbottom="30px">
                    <ControlContainerRow padright="10px" padleft="20px" padtop="10px">
                      <ControlContainer justify="left" flex="3">
                        <PageSection display="flex" flexDirection="column">
                          <Subtitle>{selectedOptionName}</Subtitle>
                          <Caption>
                            Culture scores compared to overall organization score
                          </Caption>
                        </PageSection>
                      </ControlContainer>
                      <ControlContainer justify="right">
                        <DoughnutGauge
                          complete={selectedScore ? selectedScore : 0}
                          percent={false}
                          size="tiny"
                          max={10}
                          precision={1}
                          title="Overall Score"
                          noWrap={true}
                        />
                      </ControlContainer>
                    </ControlContainerRow>
                  </PageSection>

                  {type === 'bar' ? (
                    <AnalyticsOverviewBarChart
                      data={pillarData}
                      benchmarkData={benchmarkPillarData}
                      showBenchmarkLegend={false}
                      benchmarkVisible={showBenchmarkLine}
                      multiple={10}
                      percent={false}
                      fontSize="12px"
                      barHeight="20px"
                      barPadding="9px"
                    />
                  ) : (
                    <AnalyticsOverviewRadarChart
                      data1={pillarData}
                      label1={currentPeriodName}
                      data2={priorPillarData}
                      label2={priorPeriodName}
                      data3={visibleRadarBenchmarkPillarData}
                      label3="nuLogic Benchmark"
                    />
                  )}
                </PageSection>
              </PageSection>
            </RightColumnCard>
          </RightColumnSection>
        </RightColumn>
      </AnalyticsOverviewRow>
    </>
  );
};

export default AnalyticsOverviewBar;

const AnalyticsOverviewRow = styled(PageSection)`
  width: 100%;
  display: flex;
  align-items: stretch;
  gap: 10px;
  height: 100%;

  @media (max-width: 1100px) {
    flex-direction: column;
    height: auto;
  }
`;

const LeftColumn = styled.div`
  flex: 3;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-right: 15px;
  min-width: 360px;

  @media (max-width: 1100px) {
    width: 100%;
    padding-right: 0;
    min-width: 0;
  }
`;

const RightColumn = styled.div`
  flex: 2;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  min-width: 360px;

  @media (max-width: 1100px) {
    min-width: 0;
  }
`;

const RightColumnSection = styled(PageSection)`
  display: flex;
  flex: 1;
  min-height: 0;
`;

const RightColumnCard = styled(StyledCard)`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
`;

const TopRightControls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: nowrap;
  justify-content: flex-end;

  > * {
    flex-shrink: 0;
  }
`;

const BenchmarkToggle = styled.div`
  display: inline-flex;
  align-items: center;
  height: 38px;
  gap: 8px;
  white-space: nowrap;
  border: none;
  border-radius: 0;
  background: #fff;
  color: #666;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  padding: 0 10px;
  box-sizing: border-box;
`;

const BenchmarkCheckbox = styled.input`
  cursor: pointer;
`;

const BenchmarkLegendSwatch = styled.span<{ hidden: boolean }>`
  width: 22px;
  height: 0;
  border-top: 2px dashed rgba(230, 101, 121, 0.95);
  opacity: ${({ hidden }) => (hidden ? 0.3 : 1)};
`;

const BenchmarkControlRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: -10px;
  padding: 0 0 4px 0;
`;

